import { textColorFields } from '../_shared/textColor.js';
import { universalFields } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
/**
 * contact-form — Shrine "Contact form" at config-by-config parity. A heading (+ the shared word-HIGHLIGHT
 * engine and the shared 13-pattern background) over a real, posting `<form>` whose inputs are authored as
 * inline CHILD BLOCKS exactly like Shrine: `field_row` (two inputs in one row), `textarea` (a message box)
 * and `tnc_checkbox` (a terms-&-conditions consent block, limit 1). The form renders a fully-skinnable
 * SUCCESS state and per-field ERROR states.
 *
 * CONSOLIDATED / DEFERRED from Shrine (each with a reason, mirrored in Component.tsx):
 *  - color_scheme + the 8 custom-color overlays (custom_colors_background/_text/_solid_button_*,
 *    custom_gradient_background, custom_image_background, custom_mobile_image_background) → collapsed into the
 *    universal `colorScheme`; color comes from `var(--aqx-scheme-*)` + theme tokens (project rule: no raw
 *    section-level palettes). The image backgrounds are Shopify image_picker/CDN settings.
 *  - success_icon / errors_icon (Material-Symbols GLYPH NAME) + their *_filled flags → DEFERRED: we don't
 *    ship the Material-Symbols font. Replaced by a curated inline-SVG icon picker (`successIcon`/`errorsIcon`).
 *  - success_custom_icon / errors_custom_icon → kept as `image` fields (an uploaded icon overrides the SVG),
 *    so the capability survives.
 *  - title_highlight_1/2 sub-knobs (bg-color + highlighted-*_border/radius/padding, underline-thickness/
 *    color/position, handwritten line color/stroke/position) → now IMPLEMENTED via the shared highlight
 *    engine's inspector fields (`headingHighlightFields`), wired through readHighlight → highlightCss.
 *  - title_highlight_1/2 *_gradient + underline-color--gradient → Shopify `color_background` gradient pickers;
 *    the engine falls back to a sensible default gradient (DEFAULT_HL_GRADIENT). Those raw gradient ids stay
 *    Shopify-only (the gradient style itself IS exposed via `headingHighlightN` = gradient-color).
 *  - display_id / visibility → Shopify section-chrome, no meaning off-Shopify.
 */
export const contactFormSchema = {
    type: 'contact-form',
    name: 'Contact form',
    version: 1,
    templates: ['index', 'page', 'product', 'collection', 'thank-you'],
    fields: [
        // ── General: heading ──────────────────────────────────────────────────────
        {
            id: 'title',
            type: 'richtext',
            label: 'Heading',
            group: 'General',
            default: { format: 'html', html: '<p>Contact form</p>' },
            info: 'Select words and apply Bold (B) for highlight 1, Italic (I) for highlight 2 — like the Text block.',
        },
        // Heading word-HIGHLIGHT engines 1 & 2 (Shrine title_highlight_1 / _2 + their sub-knobs).
        ...headingHighlightFields(1, 'General'),
        ...headingHighlightFields(2, 'General'),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'General',
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom heading size (desktop)',
            group: 'General',
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom heading size (mobile)',
            group: 'General',
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'buttonFullWidth',
            type: 'checkbox',
            label: 'Full-width submit button',
            group: 'General',
            default: true,
        },
        {
            id: 'buttonLabel',
            type: 'text',
            label: 'Submit button label',
            group: 'General',
            default: 'Envoyer',
            defaultKey: 'contact.send',
            maxLength: 40,
        },
        // ── Background pattern (shared engine — Shrine pattern_bg + sub-knobs). ─────
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'Background pattern',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'Background pattern',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'Background pattern',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'Background pattern',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'Background pattern',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'Background pattern',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Success message (Shrine "Success message"). ────────────────────────────
        {
            id: 'successIcon',
            type: 'select',
            label: 'Success icon',
            group: 'Success message',
            default: 'check-circle',
            info: 'Curated icon set (replaces Shrine’s Material-Symbols glyph name).',
            options: [
                { value: 'check-circle', label: 'Check circle' },
                { value: 'check', label: 'Check' },
                { value: 'thumb-up', label: 'Thumbs up' },
                { value: 'heart', label: 'Heart' },
                { value: 'sparkle', label: 'Sparkle' },
                { value: 'none', label: 'No icon' },
            ],
        },
        {
            id: 'successCustomIcon',
            type: 'image',
            label: 'Custom success icon',
            group: 'Success message',
            info: 'Overrides the icon above when set.',
        },
        {
            id: 'successText',
            type: 'text',
            label: 'Success message',
            group: 'Success message',
            default: 'Merci de nous avoir contactés ! Nous vous répondrons très vite.',
            defaultKey: 'contact.success',
            maxLength: 200,
        },
        {
            id: 'successTextColor',
            type: 'color',
            label: 'Text color',
            group: 'Success message',
            default: { ref: 'custom', value: '#28C100' },
        },
        {
            id: 'successBgColor',
            type: 'color',
            label: 'Background color',
            group: 'Success message',
            default: { ref: 'custom', value: '#F2FFED' },
        },
        {
            id: 'successBorderRadius',
            type: 'range',
            label: 'Border radius',
            group: 'Success message',
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 8,
        },
        {
            id: 'successBorderWidth',
            type: 'range',
            label: 'Border width',
            group: 'Success message',
            min: 0,
            max: 5,
            step: 1,
            unit: 'px',
            default: 1,
        },
        {
            id: 'successBorderColor',
            type: 'color',
            label: 'Border color',
            group: 'Success message',
            default: { ref: 'custom', value: '#28C100' },
            visibleWhen: { field: 'successBorderWidth', notEquals: 0 },
        },
        // ── Error messages (Shrine "Error messages"). ──────────────────────────────
        {
            id: 'errorsIcon',
            type: 'select',
            label: 'Error icon',
            group: 'Error messages',
            default: 'error',
            info: 'Curated icon set (replaces Shrine’s Material-Symbols glyph name).',
            options: [
                { value: 'error', label: 'Error (!)' },
                { value: 'warning', label: 'Warning (triangle)' },
                { value: 'info', label: 'Info' },
                { value: 'cancel', label: 'Cancel (x)' },
                { value: 'none', label: 'No icon' },
            ],
        },
        {
            id: 'errorsCustomIcon',
            type: 'image',
            label: 'Custom error icon',
            group: 'Error messages',
            info: 'Overrides the icon above when set.',
        },
        {
            id: 'errorsTextColor',
            type: 'color',
            label: 'Text color',
            group: 'Error messages',
            default: { ref: 'custom', value: '#FF0000' },
        },
        {
            id: 'errorsBgColor',
            type: 'color',
            label: 'Background color',
            group: 'Error messages',
            default: { ref: 'custom', value: '#fff5f5' },
        },
        {
            id: 'errorsBorderRadius',
            type: 'range',
            label: 'Border radius',
            group: 'Error messages',
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 8,
        },
        {
            id: 'errorsBorderWidth',
            type: 'range',
            label: 'Border width',
            group: 'Error messages',
            min: 0,
            max: 5,
            step: 1,
            unit: 'px',
            default: 1,
        },
        {
            id: 'errorsBorderColor',
            type: 'color',
            label: 'Border color',
            group: 'Error messages',
            default: { ref: 'custom', value: '#FF0000' },
            visibleWhen: { field: 'errorsBorderWidth', notEquals: 0 },
        },
        // ── Section padding (Shrine padding_top / padding_bottom) + universal. ──────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // ── Section (universal: colorScheme + width; padding overridden above). ─────
        ...universalFields.filter((f) => f.id !== 'paddingTop' && f.id !== 'paddingBottom'),
    ],
    // ── Form content: inputs authored as inline CHILD BLOCKS (Shrine field_row / textarea / tnc_checkbox).
    blocks: [
        // Shrine `field_row` — up to TWO inputs in one row.
        {
            type: 'field_row',
            name: 'Input row',
            fields: [
                // Input #1
                {
                    id: 'input1Enabled',
                    type: 'checkbox',
                    label: 'Show input #1',
                    group: 'Input #1',
                    default: true,
                },
                {
                    id: 'input1Type',
                    type: 'select',
                    label: 'Input #1 type',
                    group: 'Input #1',
                    default: 'custom',
                    options: [
                        { value: 'name', label: 'Name' },
                        { value: 'email', label: 'Email' },
                        { value: 'phone', label: 'Phone' },
                        { value: 'custom', label: 'Custom' },
                    ],
                    visibleWhen: { field: 'input1Enabled', equals: true },
                },
                {
                    id: 'input1CustomName',
                    type: 'text',
                    label: 'Input #1 label',
                    group: 'Input #1',
                    default: 'Champ personnalisé',
                    defaultKey: 'contact.custom_field',
                    visibleWhen: { field: 'input1Type', equals: 'custom' },
                },
                {
                    id: 'input1Required',
                    type: 'checkbox',
                    label: 'Required',
                    group: 'Input #1',
                    default: false,
                    info: 'Email inputs are always required.',
                    visibleWhen: { field: 'input1Enabled', equals: true },
                },
                {
                    id: 'input1ErrorMessage',
                    type: 'text',
                    label: 'Error message',
                    group: 'Input #1',
                    default: 'Ce champ est obligatoire',
                    defaultKey: 'contact.field_required',
                    visibleWhen: { field: 'input1Enabled', equals: true },
                },
                // Input #2
                {
                    id: 'input2Enabled',
                    type: 'checkbox',
                    label: 'Show input #2',
                    group: 'Input #2',
                    default: false,
                },
                {
                    id: 'input2Type',
                    type: 'select',
                    label: 'Input #2 type',
                    group: 'Input #2',
                    default: 'custom',
                    options: [
                        { value: 'name', label: 'Name' },
                        { value: 'email', label: 'Email' },
                        { value: 'phone', label: 'Phone' },
                        { value: 'custom', label: 'Custom' },
                    ],
                    visibleWhen: { field: 'input2Enabled', equals: true },
                },
                {
                    id: 'input2CustomName',
                    type: 'text',
                    label: 'Input #2 label',
                    group: 'Input #2',
                    default: 'Champ personnalisé',
                    defaultKey: 'contact.custom_field',
                    visibleWhen: { field: 'input2Type', equals: 'custom' },
                },
                {
                    id: 'input2Required',
                    type: 'checkbox',
                    label: 'Required',
                    group: 'Input #2',
                    default: false,
                    info: 'Email inputs are always required.',
                    visibleWhen: { field: 'input2Enabled', equals: true },
                },
                {
                    id: 'input2ErrorMessage',
                    type: 'text',
                    label: 'Error message',
                    group: 'Input #2',
                    default: 'Ce champ est obligatoire',
                    defaultKey: 'contact.field_required',
                    visibleWhen: { field: 'input2Enabled', equals: true },
                },
            ],
        },
        // Shrine `textarea` — multi-line text box.
        {
            type: 'textarea',
            name: 'Text box',
            fields: [
                {
                    id: 'inputType',
                    type: 'select',
                    label: 'Type',
                    default: 'custom',
                    options: [
                        { value: 'comment', label: 'Comment' },
                        { value: 'custom', label: 'Custom' },
                    ],
                },
                {
                    id: 'inputCustomName',
                    type: 'text',
                    label: 'Label',
                    default: 'Champ personnalisé',
                    defaultKey: 'contact.custom_field',
                    visibleWhen: { field: 'inputType', equals: 'custom' },
                },
                {
                    id: 'maxCharacters',
                    type: 'number',
                    label: 'Max characters',
                    min: 0,
                    step: 1,
                },
                {
                    id: 'inputRequired',
                    type: 'checkbox',
                    label: 'Required',
                    default: false,
                },
                {
                    id: 'inputErrorMessage',
                    type: 'text',
                    label: 'Error message',
                    default: 'Ce champ est obligatoire',
                    defaultKey: 'contact.field_required',
                },
            ],
        },
        // Shrine `tnc_checkbox` — terms & conditions consent checkbox (limit 1).
        {
            type: 'tnc_checkbox',
            name: 'Policies checkbox',
            max: 1,
            fields: [
                {
                    id: 'checkboxColor',
                    type: 'select',
                    label: 'Checkbox color',
                    group: 'Checkbox & text',
                    default: 'text',
                    options: [
                        { value: 'accent-1', label: 'Accent 1' },
                        { value: 'accent-2', label: 'Accent 2' },
                        { value: 'background-1', label: 'Background 1' },
                        { value: 'background-2', label: 'Background 2' },
                        { value: 'outline-button', label: 'Outline button' },
                        { value: 'text', label: 'Text' },
                    ],
                },
                {
                    id: 'checkboxText',
                    type: 'text',
                    label: 'Checkbox text',
                    group: 'Checkbox & text',
                    default: 'J’accepte les conditions générales.',
                    defaultKey: 'cart.terms_accept',
                    maxLength: 200,
                },
                {
                    id: 'checkboxTextSize',
                    type: 'range',
                    label: 'Text size',
                    group: 'Checkbox & text',
                    min: 12,
                    max: 22,
                    step: 1,
                    unit: 'px',
                    default: 17,
                },
                {
                    id: 'checkboxAlignment',
                    type: 'select',
                    label: 'Alignment',
                    group: 'Checkbox & text',
                    default: 'left',
                    options: [
                        { value: 'left', label: 'Left' },
                        { value: 'center', label: 'Center' },
                        { value: 'right', label: 'Right' },
                    ],
                },
                {
                    id: 'warningPosition',
                    type: 'select',
                    label: 'Warning position',
                    group: 'Warning message',
                    default: 'under-button',
                    options: [
                        { value: 'under-checkbox', label: 'Under checkbox' },
                        { value: 'above-button', label: 'Above button' },
                        { value: 'under-button', label: 'Under button' },
                    ],
                },
                {
                    id: 'warningText',
                    type: 'text',
                    label: 'Warning text',
                    group: 'Warning message',
                    default: 'Vous devez accepter les conditions générales.',
                    defaultKey: 'cart.terms_required',
                    maxLength: 200,
                },
                {
                    id: 'warningTextSize',
                    type: 'range',
                    label: 'Warning text size',
                    group: 'Warning message',
                    min: 12,
                    max: 22,
                    step: 1,
                    unit: 'px',
                    default: 14,
                },
                {
                    id: 'warningAlignment',
                    type: 'select',
                    label: 'Warning alignment',
                    group: 'Warning message',
                    default: 'left',
                    options: [
                        { value: 'left', label: 'Left' },
                        { value: 'center', label: 'Center' },
                    ],
                },
                {
                    id: 'warningTextColor',
                    type: 'color',
                    label: 'Warning text color',
                    group: 'Warning message',
                    default: { ref: 'custom', value: '#ff0000' },
                },
                {
                    id: 'disableSubmitButton',
                    type: 'checkbox',
                    label: 'Disable submit until checked',
                    group: 'Warning message',
                    default: false,
                },
                {
                    id: 'marginTop',
                    type: 'range',
                    label: 'Margin top',
                    group: 'Block margin',
                    min: 0,
                    max: 45,
                    step: 3,
                    unit: 'px',
                    default: 0,
                },
                {
                    id: 'marginBottom',
                    type: 'range',
                    label: 'Margin bottom',
                    group: 'Block margin',
                    min: 0,
                    max: 45,
                    step: 3,
                    unit: 'px',
                    default: 15,
                },
            ],
        },
    ],
};
export const contactFormPresets = [
    {
        name: 'Contact form',
        settings: {
            title: { format: 'html', html: '<p>Contactez-<strong>nous</strong></p>' },
            headingHighlight1: 'underline-solid',
            headingSize: 'h1',
            buttonFullWidth: true,
            // `buttonLabel` and `successText` were repeated here, identical to the fields' own defaults — a
            // preset is spread OVER the defaults, so the copies would have put French back.
            // A clean LIGHT contact band, typically near the bottom of the home page.
            colorScheme: 'light',
            width: 'narrow',
            paddingTop: 56,
            paddingBottom: 56,
        },
        // Faithful Shrine default preset: name + email row, phone row, comment box, policies checkbox.
        blocks: [
            {
                type: 'field_row',
                settings: {
                    input1Enabled: true,
                    input1Type: 'name',
                    input2Enabled: true,
                    input2Type: 'email',
                },
            },
            {
                type: 'field_row',
                settings: {
                    input1Enabled: true,
                    input1Type: 'phone',
                    input2Enabled: false,
                },
            },
            {
                type: 'textarea',
                settings: {
                    inputType: 'comment',
                    inputRequired: false,
                },
            },
            {
                type: 'tnc_checkbox',
                settings: {
                    warningPosition: 'under-button',
                    marginTop: 6,
                    marginBottom: 15,
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const contactFormMeta = {
    label: 'Contact form',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map